import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { apiClient } from '@/lib/apiClient';
import { Button } from '@/components/ui/Button';
import type { MeCapabilitiesDto, ModuleStateDto } from '@/hooks/useCapabilities';

/**
 * Story 121.9 — PLATFORM_ADMIN module storefront.
 *
 * Pick a tenant by UUID, see its module state, grant or revoke explicit
 * entitlements (source=GRANT). Foundation modules show as locked.
 * Errors from the backend (CONFLICT with a problem-detail `kind`) surface
 * inline.
 *
 * This is the platform-admin counterpart to the tenant-admin storefront
 * (story 121.10, `/admin/modules`).
 */
export function AdminModuleStorefrontPage() {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const initialTenant = params.get('tenant') ?? '';
  const [tenantId, setTenantId] = useState(initialTenant);
  const [data, setData] = useState<MeCapabilitiesDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(
    async (id: string) => {
      if (!id) {
        setData(null);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const { data: payload } = await apiClient.get<MeCapabilitiesDto>(
          `/admin/tenants/${id}/modules`,
        );
        setData(payload);
      } catch {
        setError(t('modules.errors.loadFailed', { defaultValue: 'Could not load modules' }));
        setData(null);
      } finally {
        setLoading(false);
      }
    },
    [t],
  );

  useEffect(() => {
    if (initialTenant) void load(initialTenant);
  }, [initialTenant, load]);

  const submitTenantPick = () => {
    setParams({ tenant: tenantId });
    void load(tenantId);
  };

  const entitle = async (key: string) => {
    setError(null);
    try {
      const { data: payload } = await apiClient.post<MeCapabilitiesDto>(
        `/admin/tenants/${tenantId}/modules/${key}/entitle`,
      );
      setData(payload);
    } catch (e) {
      const detail = (e as { response?: { data?: { detail?: string } } }).response?.data?.detail;
      setError(detail ?? t('modules.errors.grantFailed', { defaultValue: 'Could not grant' }));
    }
  };

  const revoke = async (key: string) => {
    setError(null);
    try {
      const { data: payload } = await apiClient.delete<MeCapabilitiesDto>(
        `/admin/tenants/${tenantId}/modules/${key}/entitle`,
      );
      setData(payload);
    } catch (e) {
      const detail = (e as { response?: { data?: { detail?: string } } }).response?.data?.detail;
      setError(
        detail ?? t('modules.errors.revokeFailed', { defaultValue: 'Could not revoke' }),
      );
    }
  };

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold">
        {t('modules.admin.title', { defaultValue: 'Modules — platform admin' })}
      </h1>

      <div className="flex items-end gap-2">
        <div className="flex-1">
          <label
            htmlFor="admin-modules-tenant-input"
            className="block text-xs uppercase text-muted-foreground"
          >
            {t('modules.admin.tenantId', { defaultValue: 'Tenant UUID' })}
          </label>
          <input
            id="admin-modules-tenant-input"
            data-testid="admin-modules-tenant"
            value={tenantId}
            onChange={(e) => setTenantId(e.target.value)}
            className="mt-1 w-full rounded border border-sidebar-border bg-card px-3 py-2 text-sm"
            placeholder="00000000-0000-0000-0000-000000000000"
          />
        </div>
        <Button
          data-testid="admin-modules-load"
          onClick={submitTenantPick}
          disabled={!tenantId || loading}
        >
          {t('modules.admin.load', { defaultValue: 'Load' })}
        </Button>
      </div>

      {error && (
        <div
          data-testid="admin-modules-error"
          className="rounded border border-red-700 bg-red-950/40 p-3 text-sm text-red-200"
        >
          {error}
        </div>
      )}

      {data && (
        <div className="rounded-md border border-sidebar-border">
          <table className="w-full text-sm">
            <thead className="bg-muted/30 text-left text-xs uppercase text-muted-foreground">
              <tr>
                <th className="px-3 py-2">{t('modules.col.module', { defaultValue: 'Module' })}</th>
                <th className="px-3 py-2">{t('modules.col.tier', { defaultValue: 'Tier' })}</th>
                <th className="px-3 py-2">{t('modules.col.entitled', { defaultValue: 'Entitled' })}</th>
                <th className="px-3 py-2">{t('modules.col.activated', { defaultValue: 'Activated' })}</th>
                <th className="px-3 py-2">{t('modules.col.source', { defaultValue: 'Source' })}</th>
                <th className="px-3 py-2 text-right">{t('modules.col.actions', { defaultValue: 'Actions' })}</th>
              </tr>
            </thead>
            <tbody>
              {data.states.map((s) => (
                <ModuleRow key={s.key} state={s} onEntitle={entitle} onRevoke={revoke} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ModuleRow({
  state,
  onEntitle,
  onRevoke,
}: {
  state: ModuleStateDto;
  onEntitle: (key: string) => void;
  onRevoke: (key: string) => void;
}) {
  const { t } = useTranslation();
  const isExplicitGrant = state.source === 'GRANT' || state.source === 'PURCHASE';
  return (
    <tr data-testid={`admin-modules-row-${state.key}`} className="border-t border-sidebar-border">
      <td className="px-3 py-2 font-medium">
        {state.name}
        <div className="text-xs text-muted-foreground">{state.key}</div>
      </td>
      <td className="px-3 py-2">{state.tierFloor}</td>
      <td className="px-3 py-2">{state.entitled ? '✓' : '—'}</td>
      <td className="px-3 py-2">{state.activated ? '✓' : '—'}</td>
      <td className="px-3 py-2 text-xs text-muted-foreground">{state.source ?? '—'}</td>
      <td className="px-3 py-2 text-right space-x-2">
        {state.foundation ? (
          <span className="text-xs text-muted-foreground">
            {t('modules.foundation', { defaultValue: 'foundation' })}
          </span>
        ) : isExplicitGrant ? (
          <Button
            variant="outline"
            size="sm"
            data-testid={`admin-modules-revoke-${state.key}`}
            onClick={() => onRevoke(state.key)}
          >
            {t('modules.admin.revoke', { defaultValue: 'Revoke' })}
          </Button>
        ) : (
          <Button
            size="sm"
            data-testid={`admin-modules-entitle-${state.key}`}
            onClick={() => onEntitle(state.key)}
          >
            {t('modules.admin.entitle', { defaultValue: 'Grant' })}
          </Button>
        )}
      </td>
    </tr>
  );
}
